import React from 'react';

/**
 * update 2026-06-25.1 (revised 2026-07-13)
 *
 * Renders a row of 5 small star icons representing an OpenScore feedback
 * rating (0-5, one decimal place). Stars are filled in proportion to the
 * rating — e.g. 4.3 renders four full stars and a 30%-filled fifth star.
 * Filled portions use yellow (#f5c518); the remainder is greyed.
 */

const FILL_COLOR = '#f5c518';
const EMPTY_COLOR = '#d5d8dc';
const STAR_PATH =
    'M12 2.6l2.94 5.96 6.58.96-4.76 4.64 1.12 6.55L12 18.2l-5.88 3.1 1.12-6.55L2.48 9.52l6.58-.96L12 2.6z';

interface FeedbackStarsProps {
    rating: number;
    size?: number;
    gap?: number;
}

const FeedbackStars: React.FC<FeedbackStarsProps> = ({ rating, size = 13, gap = 1 }) => {
    const clamped = Math.round(Math.min(5, Math.max(0, rating)) * 10) / 10;
    const ariaValue = Number.isInteger(clamped) ? `${clamped}` : clamped.toFixed(1);
    // Unique-enough id prefix so partial-star gradients never collide between cards.
    const gradientIdPrefix = React.useId();

    const partials: { id: string; fraction: number }[] = [];

    const stars = Array.from({ length: 5 }, (_, i) => {
        // How much of this star is filled: 0 (empty) .. 1 (full).
        const fraction = Math.min(1, Math.max(0, clamped - i));

        let fill: string;
        if (fraction >= 1) {
            fill = FILL_COLOR;
        } else if (fraction <= 0) {
            fill = EMPTY_COLOR;
        } else {
            const id = `${gradientIdPrefix}-star-${i}`;
            partials.push({ id, fraction });
            fill = `url(#${id})`;
        }

        return (
            <svg
                key={i}
                viewBox="0 0 24 24"
                width={size}
                height={size}
                aria-hidden="true"
                focusable="false"
                style={{ display: 'block', flex: '0 0 auto' }}
            >
                <path d={STAR_PATH} fill={fill} />
            </svg>
        );
    });

    return (
        <span
            role="img"
            aria-label={`OpenScore feedback rating: ${ariaValue} out of 5`}
            style={{ display: 'inline-flex', alignItems: 'center', gap: `${gap}px` }}
        >
            {partials.length > 0 && (
                <svg width="0" height="0" style={{ position: 'absolute' }} aria-hidden="true">
                    <defs>
                        {partials.map(({ id, fraction }) => (
                            <linearGradient key={id} id={id} x1="0" y1="0" x2="1" y2="0">
                                <stop offset={`${fraction * 100}%`} stopColor={FILL_COLOR} />
                                <stop offset={`${fraction * 100}%`} stopColor={EMPTY_COLOR} />
                            </linearGradient>
                        ))}
                    </defs>
                </svg>
            )}
            {stars}
        </span>
    );
};

export default FeedbackStars;
